/**
 * ScrollAnimations.tsx
 * Återanvändbara scroll-triggered animationskomponenter med framer-motion.
 * First paint (SSR + hydrate) is a plain element. Motion attaches after mount
 * so inline opacity/transform cannot mismatch React #418.
 * framer-motion is imported after idle/interaction so vendor-motion is
 * not on the Lighthouse critical request chain.
 */
import React, { type ComponentType, type ReactNode, useEffect, useState } from "react";
import { whenBrowserIdle } from "@/lib/whenBrowserIdle";

type MotionNodes = {
  div: ComponentType<Record<string, unknown>>;
  span: ComponentType<Record<string, unknown>>;
};

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

let motionPromise: Promise<MotionNodes> | null = null;

function loadMotion(): Promise<MotionNodes> {
  if (!motionPromise) {
    motionPromise = import("framer-motion").then((mod) => ({
      div: mod.motion.div,
      span: mod.motion.span,
    }));
  }
  return motionPromise;
}

function useMotionReady(): { reduced: boolean; ready: boolean; motion: MotionNodes | null } {
  const reduced = usePrefersReducedMotion();
  const [motion, setMotion] = useState<MotionNodes | null>(null);
  useEffect(() => {
    let alive = true;
    const stop = whenBrowserIdle(() => {
      loadMotion().then((nodes) => {
        if (alive) setMotion(nodes);
      });
    });
    return () => {
      alive = false;
      stop();
    };
  }, []);
  return { reduced, ready: motion !== null, motion };
}

const viewport = { once: true, margin: "-60px" };

const fadeUpVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0 },
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const fadeLeftVariants = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0 },
};

const fadeRightVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0 },
};

const scaleInVariants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1 },
};

const reducedVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const staggerItemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const reducedStaggerItemVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

interface AnimationProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  instant?: boolean;
}

interface StaggerProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
}

function StaticBox({
  children,
  className,
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "span";
}) {
  return <Tag className={className}>{children}</Tag>;
}

export function FadeUp({ children, className, delay = 0, duration = 0.6 }: AnimationProps) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox className={className}>{children}</StaticBox>;
  const MotionDiv = motion.div;
  return (
    <MotionDiv
      variants={reduced ? reducedVariants : fadeUpVariants}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      transition={{ duration: reduced ? 0.01 : duration, delay: reduced ? 0 : delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}

export function FadeIn({ children, className, delay = 0, duration = 0.7 }: AnimationProps) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox className={className}>{children}</StaticBox>;
  const MotionDiv = motion.div;
  return (
    <MotionDiv
      variants={reduced ? reducedVariants : fadeInVariants}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      transition={{ duration: reduced ? 0.01 : duration, delay: reduced ? 0 : delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}

export function FadeLeft({ children, className, delay = 0, duration = 0.6 }: AnimationProps) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox className={className}>{children}</StaticBox>;
  const MotionDiv = motion.div;
  return (
    <MotionDiv
      variants={reduced ? reducedVariants : fadeLeftVariants}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      transition={{ duration: reduced ? 0.01 : duration, delay: reduced ? 0 : delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}

export function FadeRight({ children, className, delay = 0, duration = 0.6 }: AnimationProps) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox className={className}>{children}</StaticBox>;
  const MotionDiv = motion.div;
  return (
    <MotionDiv
      variants={reduced ? reducedVariants : fadeRightVariants}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      transition={{ duration: reduced ? 0.01 : duration, delay: reduced ? 0 : delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}

export function ScaleIn({ children, className, delay = 0, duration = 0.5 }: AnimationProps) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox className={className}>{children}</StaticBox>;
  const MotionDiv = motion.div;
  return (
    <MotionDiv
      variants={reduced ? reducedVariants : scaleInVariants}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      transition={{ duration: reduced ? 0.01 : duration, delay: reduced ? 0 : delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}

export function StaggerContainer({ children, className, staggerDelay = 0.1 }: StaggerProps) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox className={className}>{children}</StaticBox>;
  const MotionDiv = motion.div;
  return (
    <MotionDiv
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: reduced ? 0 : staggerDelay,
            delayChildren: reduced ? 0 : 0.05,
          },
        },
      }}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}

export function StaggerItem({ children, className, duration = 0.5 }: AnimationProps) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox className={className}>{children}</StaticBox>;
  const MotionDiv = motion.div;
  return (
    <MotionDiv
      variants={reduced ? reducedStaggerItemVariants : staggerItemVariants}
      transition={{ duration: reduced ? 0.01 : duration, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}

export function HeroReveal({ children, className, delay = 0.2, instant = false }: AnimationProps) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox className={className}>{children}</StaticBox>;
  const MotionDiv = motion.div;
  const skip = instant || reduced;
  return (
    <MotionDiv
      initial={skip ? false : { opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: skip ? 0 : 0.8, delay: skip ? 0 : delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}

export function CountUp({ children, className }: { children: ReactNode; className?: string }) {
  const { reduced, ready, motion } = useMotionReady();
  if (!ready || !motion) return <StaticBox as="span" className={className}>{children}</StaticBox>;
  const MotionSpan = motion.span;
  return (
    <MotionSpan
      initial={{ opacity: 0, y: reduced ? 0 : 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: reduced ? 0.01 : 0.4, ease: "easeOut" }}
      className={className}
    >
      {children}
    </MotionSpan>
  );
}
